import { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";
import { getV2FallbackPath } from "../utils/v2Navigation";

/**
 * Hook pour vérifier l'accès d'un utilisateur à une page spécifique
 * @param {string} pageName - Le nom de la page (ex: "dashboard-v2")
 * @param {string|number} hotelId - L'ID de l'hôtel
 * @param {object} options - Options supplémentaires
 * @param {boolean} options.redirectOnDenied - Rediriger vers le dashboard si accès refusé (défaut: true)
 * @param {string} options.redirectPath - Chemin de redirection si accès refusé (défaut: "/hotel/lunishower/v2/dashboard")
 * @param {boolean} options.requireHotel - Exiger un hôtel pour vérifier l'accès (défaut: true). Si false, permet l'accès sans hôtel (mode dev/test)
 * @returns {object} { hasAccess: boolean, loading: boolean, error: string|null }
 */
export const usePageAccess = (pageName, hotelId, options = {}) => {
    const { 
        redirectOnDenied = true, 
        redirectPath = "/hotel/lunishower/v2/dashboard",
        requireHotel = true 
    } = options;
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [hasAccess, setHasAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const checkAccess = async () => {
            // Si pas authentifié, pas d'accès
            if (!auth.isAuthenticated) {
                setHasAccess(false);
                setLoading(false);
                if (redirectOnDenied) {
                    navigate(redirectPath);
                }
                return;
            }

            // Si pas d'hôtel et que requireHotel est false, autoriser l'accès (mode test)
            // On vérifie juste que la page existe et est visible, mais on ne bloque pas si l'API échoue
            if (!hotelId && !requireHotel) {
                try {
                    setLoading(true);
                    // Vérifier que la page existe et est visible (sans vérifier l'accès utilisateur/hôtel)
                    // On inclut les pages invisibles pour pouvoir vérifier le flag pVisible
                    const response = await fetch(`/api/pages/${pageName}?includeInvisible=true`, {
                        method: "GET",
                        headers: {
                            Authorization: "Bearer " + auth.user?.access_token,
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    });

                    if (response.ok) {
                        const data = await response.json();
                        // Autoriser l'accès si la page existe et est visible
                        setHasAccess(data.page?.pVisible === 1);
                        setError(null);
                    } else {
                        setHasAccess(true);
                        setError(null);
                    }
                } catch (err) {
                    setHasAccess(true);
                    setError(null);
                } finally {
                    setLoading(false);
                }
                return;
            }

            // Si pas d'hôtel et que requireHotel est true, bloquer l'accès
            if (!hotelId && requireHotel) {
                setHasAccess(false);
                setLoading(false);
                if (redirectOnDenied) {
                    navigate(redirectPath);
                }
                return;
            }

            try {
                setLoading(true);
                const response = await fetch(`/api/pages/${pageName}/access?hId=${hotelId}`, {
                    method: "GET",
                    headers: {
                        Authorization: "Bearer " + auth.user?.access_token,
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    const access = data.hasAccess === true;

                    setHasAccess(access);
                    setError(null);

                    // Rediriger si accès refusé
                    if (!access && redirectOnDenied) {
                        const returnTo = getV2FallbackPath(location.state);
                        navigate(returnTo, { replace: true });
                    }
                } else {
                    // Erreur API (403, 404, etc.)
                    const errorData = await response.json().catch(() => ({}));
                    console.error(`❌ API error checking access for "${pageName}":`, response.status, errorData);
                    setHasAccess(false);
                    setError("Access denied");
                    if (redirectOnDenied) {
                        navigate(getV2FallbackPath(location.state), { replace: true });
                    }
                }
            } catch (err) {
                console.error(`❌ Error checking page access for "${pageName}":`, err);
                setHasAccess(false);
                setError(err.message);
                if (redirectOnDenied) {
                    navigate(getV2FallbackPath(location.state), { replace: true });
                }
            } finally {
                setLoading(false);
            }
        };

        checkAccess();
        // NOTE: Ne pas dépendre de auth.user?.access_token ici.
        // Le token peut être renouvelé périodiquement par OIDC, ce qui provoque des re-fetch
        // et un effet "refresh" global alors qu'on reste sur la même page.
    }, [pageName, hotelId, auth.isAuthenticated, navigate, redirectOnDenied, redirectPath, requireHotel, location.state]);

    return { hasAccess, loading, error };
};

/**
 * Hook pour récupérer toutes les pages accessibles pour un utilisateur et un hôtel
 * @param {string|number} hotelId - L'ID de l'hôtel
 * @returns {object} { pages: array, loading: boolean, error: string|null }
 */
export const useAccessiblePages = (hotelId) => {
    const auth = useAuth();
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAccessiblePages = async () => {
            if (!auth.isAuthenticated || !hotelId) {
                setPages([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await fetch(`/api/pages/accessible?hId=${hotelId}`, {
                    method: "GET",
                    headers: {
                        Authorization: "Bearer " + auth.user?.access_token,
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setPages(data.pages || []);
                    setError(null);
                } else {
                    setPages([]);
                    setError("Failed to fetch accessible pages");
                }
            } catch (err) {
                console.error("Error fetching accessible pages:", err);
                setPages([]);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAccessiblePages();
        // NOTE: ne pas retrigger sur changement de token (effet refresh)
    }, [hotelId, auth.isAuthenticated]);

    return { pages, loading, error };
};

